import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { Contact, ContactGroup } from '../../types';
import { listContacts, getContactDisplayName, getContactInitials } from '../../services/contacts';
import { getContactGroup, addContactsToGroup, removeContactFromGroup } from '../../services/contact-groups';

interface ContactGroupMembersDialogProps {
  open: boolean;
  onClose: () => void;
  group: ContactGroup;
  onUpdated: () => void;
}

export default function ContactGroupMembersDialog({
  open,
  onClose,
  group,
  onUpdated,
}: ContactGroupMembersDialogProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [memberContacts, setMemberContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [contacts, groupData] = await Promise.all([
        listContacts(),
        getContactGroup(group.id),
      ]);
      setAllContacts(contacts);
      const memberIds = new Set(groupData.contactIds);
      setMemberContacts(contacts.filter((c) => memberIds.has(c.id)));
    } catch (err) {
      console.error('Failed to load group members:', err);
    } finally {
      setLoading(false);
    }
  }, [group.id]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const handleAdd = async () => {
    if (!selectedContact) return;
    try {
      await addContactsToGroup(group.id, [selectedContact.id]);
      setMemberContacts((prev) => [...prev, selectedContact]);
      setSelectedContact(null);
      onUpdated();
    } catch (err) {
      console.error('Failed to add contact:', err);
    }
  };

  const handleRemove = async (contactId: string) => {
    try {
      await removeContactFromGroup(group.id, contactId);
      setMemberContacts((prev) => prev.filter((c) => c.id !== contactId));
      onUpdated();
    } catch (err) {
      console.error('Failed to remove contact:', err);
    }
  };

  const memberIds = new Set(memberContacts.map((c) => c.id));
  const availableContacts = allContacts.filter((c) => !memberIds.has(c.id));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={mobile}>
      <DialogTitle>Members — {group.name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
              <Autocomplete
                value={selectedContact}
                onChange={(_, value) => setSelectedContact(value)}
                options={availableContacts}
                getOptionLabel={getContactDisplayName}
                renderInput={(params) => (
                  <TextField {...params} label="Add contact" size="small" />
                )}
                sx={{ flex: 1 }}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={!selectedContact}
                size="small"
              >
                Add
              </Button>
            </Box>

            {memberContacts.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No members yet.
              </Typography>
            ) : (
              <List disablePadding>
                {memberContacts.map((c) => (
                  <ListItem
                    key={c.id}
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => handleRemove(c.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={c.photoUrl || undefined} sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {getContactInitials(c)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getContactDisplayName(c)}
                      secondary={c.email || undefined}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
